import {
  type DisplayContact,
  type EventContact,
  type EventType,
  type ParsedAsset,
} from '@aikoa/common'
import { useTranslations, type TFunction } from '@aikoa/i18n/react'
import dayjs from 'dayjs'
import { useRouter } from 'expo-router'
import { orderBy } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { RefreshControl, View } from 'react-native'
import { PropertyListItem } from '~/components/assets/PropertyListItem'
import { ContactListItem } from '~/components/contacts/ContactListItem'
import { ContactSelect } from '~/components/contacts/ContactSelect'
import { AddDateInline } from '~/components/events/AddDateInline'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { ViewTitle } from '~/components/layout/Sections'
import { ValueList, type ValueItem } from '~/components/layout/ValueList'
import { LoadingView } from '~/components/LoadingView'
import { ScrollView } from '~/components/ScrollView'
import { Body, Detail } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import { Badge } from '~/components/ui/badge'
import { ButtonLabel, LinkButton } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { useContacts } from '~/data/contacts'
import { useAddContactToEvent, useDeleteEvent, useEvent } from '~/data/events/useEvents'
import { cn } from '~/lib/utils'

type EventViewProps = {
  eventId: string
}

function getEventTypeLabel(eventType: string, t: TFunction<'events'>): string {
  switch (eventType) {
    case 'showing':
      return t(($) => $.eventTypes.showing)
    case 'appraisal':
      return t(($) => $.eventTypes.appraisal)
    default:
      return eventType
  }
}

function EventView({ eventId }: EventViewProps) {
  const { t } = useTranslations('events')
  const { data: event, isLoading, refetch, isRefetching } = useEvent(eventId)

  const router = useRouter()

  const { mutate: deleteEvent } = useDeleteEvent()
  const { mutate: addContact } = useAddContactToEvent()
  const { data: allContacts, isLoading: isLoadingContacts } = useContacts()

  const existingContactIds = useMemo(
    () => event?.contacts?.map((contact) => contact.id) ?? [],
    [event?.contacts]
  )

  const sortedContacts = useMemo(
    () =>
      orderBy(
        allContacts?.filter((contact) => !!contact.name && (!!contact.email || !!contact.phone)) ||
          [],
        'name'
      ),
    [allContacts]
  )

  const handleAddContact = useCallback(
    (contactId: string) => {
      if (existingContactIds.includes(contactId)) {
        return
      }

      addContact(
        { eventId, contactId },
        {
          onSuccess: () => {
            void refetch()
          },
        }
      )
    },
    [addContact, eventId, refetch, existingContactIds]
  )

  const handleDelete = useCallback(() => {
    deleteEvent(eventId, {
      onSuccess: () => {
        if (event?.asset_id) {
          router.replace(`/(authenticated)/property/${event.asset_id}`)
        } else {
          router.replace({
            pathname: '/(authenticated)',
            params: { view: 'assets' },
          })
        }
      },
    })
  }, [event, eventId, router, deleteEvent])

  const eventDate = useMemo(() => dayjs(event?.event_date), [event?.event_date])

  const values = useMemo<ValueItem[]>(() => {
    if (!event) {
      return []
    }

    const items: ValueItem[] = [
      {
        icon: event.event_type === 'showing' ? 'eye-arrow-right-outline' : 'scale-outline',
        value: getEventTypeLabel(event.event_type, t),
        description: t(($) => $.view.event),
      },
    ]

    items.push({
      icon: 'calendar-outline',
      value: event.event_date ? eventDate.format('DD.MM.YYYY [klo] HH:mm') : undefined,
      description: event.event_date ? t(($) => $.view.dateTime) : undefined,
      contentClassName: cn(event.event_date && 'relative flex-row flex-wrap'),
      labelClassName: cn(event.event_date && 'flex-1 self-center'),
      valueClassName: cn(event.event_date && 'order-last basis-full'),
      children: (
        <AddDateInline
          key={`add-date-${event.event_date}-${event.event_type}`}
          currentDate={event.event_date ? new Date(event.event_date) : undefined}
          currentEventType={event.event_type as EventType}
          eventId={eventId}
          onSuccess={() => refetch()}
          triggerClassName={cn(!event.event_date ? 'self-start' : 'z-10 -mb-3')}
          triggerVariant={event.event_date ? 'outline' : 'primary'}
          triggerSize={event.event_date ? 'xs' : 'lg'}
          triggerLabel={
            event.event_date ? t(($) => $.view.updateDateTime) : t(($) => $.view.addDateTime)
          }
        />
      ),
    })

    if (event.description) {
      items.push({
        icon: 'document-text-outline',
        value: event.description,
        description: t(($) => $.view.description),
      })
    }

    items.push({
      icon: 'time-outline',
      value: dayjs(event.created_at).format('DD.MM.YYYY HH:mm'),
      description: t(($) => $.view.created),
    })

    return items
  }, [event, eventDate, eventId, refetch, t])

  if (isLoading) {
    return (
      <ContentBox contentContainerClassName="items-center justify-center">
        <LoadingView />
      </ContentBox>
    )
  }

  if (!event) {
    return (
      <ContentBox contentContainerClassName="items-center justify-center py-8">
        <Text>{t(($) => $.view.notFound)}</Text>
        <LinkButton
          variant="primary"
          href={{ pathname: '/(authenticated)', params: { view: 'assets' } }}
          className="mt-6"
          size="lg">
          <ButtonLabel>{t(($) => $.view.back)}</ButtonLabel>
        </LinkButton>
      </ContentBox>
    )
  }

  return (
    <>
      <ScrollView
        className={cn('flex-1 rounded-content transition-opacity duration-150')}
        offsetTrackHeight
        contentContainerClassName="gap-3"
        refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} />}>
        <ContentBox
          scrollable={false}
          contentContainerClassName="justify-start gap-4 p-0 pt-4"
          header={
            <BoxActionsHeader
              contextMenuActions={[
                {
                  key: 'delete-event',
                  label: t(($) => $.view.deleteAction),
                  onPress: handleDelete,
                  icon: 'trash-2',
                  iconLibrary: 'feather',
                  labelClassName: 'text-destructive group-hover/button:text-destructive-foreground',
                  iconClassName: 'text-destructive group-hover/button:text-destructive-foreground',
                },
              ]}>
              <View className="relative w-full items-center gap-2">
                <Heading2 className="text-center">{event.title}</Heading2>
                <Badge variant="secondary" size="sm">
                  {getEventTypeLabel(event.event_type, t)}
                </Badge>
              </View>
            </BoxActionsHeader>
          }>
          {event.asset && (
            <View className="gap-4 px-3">
              <PropertyListItem
                asset={event.asset as ParsedAsset}
                organisationName={(event.asset as ParsedAsset).organisation?.name}
                realtorName={(event.asset as ParsedAsset).organisation?.realtorName}
                actions={[
                  {
                    key: 'open-property',
                    onPress: () => router.push(`/property/${event.asset_id}`),
                    label: t(($) => $.view.event),
                    buttonVariant: 'outline',
                  },
                ]}
              />
            </View>
          )}
          <ValueList
            values={values}
            className={cn('flex-1 overflow-hidden')}
            itemClassName={cn('px-6 first:border-t-0 first:pt-8 last:border-b-0')}
          />
        </ContentBox>

        <ContentBox
          scrollable={false}
          contentContainerClassName="gap-4 px-0 py-4"
          header={
            <BoxActionsHeader variant="default" className="rounded-b-none border-b border-border">
              <ViewTitle className="text-center">{t(($) => $.view.contacts)}</ViewTitle>
              <Body className="text-center text-secondary">
                {t(($) => $.view.contactsDescription)}
              </Body>
            </BoxActionsHeader>
          }>
          <View className="gap-4 px-4">
            <EventContacts contacts={event.contacts ?? []} t={t} />

            <ContactSelect
              label={t(($) => $.view.addContact)}
              sheetTitle={t(($) => $.view.addContactTitle)}
              contacts={sortedContacts}
              isLoading={isLoadingContacts}
              selectedContactIds={[]}
              className="pb-6"
              onChangeSelectedContactIds={(ids) => {
                const newContactId = ids.find((id) => !!id)

                if (newContactId) {
                  handleAddContact(newContactId)
                }
              }}
              disabledContactIds={existingContactIds}
              showCreateButton={false}
            />
          </View>
        </ContentBox>
      </ScrollView>
    </>
  )
}

export default EventView

type EventContactsProps = {
  contacts: EventContact[]
  t: TFunction<'events'>
}

function EventContacts({ contacts, t }: EventContactsProps) {
  if (contacts.length === 0) {
    return (
      <View className="flex-1 items-center justify-center py-8">
        <Heading3 className="mb-2 text-foreground">{t(($) => $.view.noContacts)}</Heading3>
        <Detail className="px-6 text-center text-foreground">
          {t(($) => $.view.noContactsDescription)}
        </Detail>
      </View>
    )
  }

  return (
    <View className="gap-2">
      {contacts.map((contact) => (
        <ContactListItem
          key={contact.id}
          contact={contact as DisplayContact}
          className="mx-0"
          linkToContact
        />
      ))}
    </View>
  )
}
